<script setup lang="ts">
import type { NuxtError } from '#app'

const { error } = defineProps<{
  error?: NuxtError
}>()

function getErrorTitle(statusCode?: number) {
  switch (statusCode) {
    case 404: return 'Página não encontrada'
    case 500: return 'Erro interno do servidor'
    case 403: return 'Acesso negado'
    case 401: return 'Não autorizado'
    default: return 'Erro do sistema'
  }
}

function getErrorDescription(statusCode?: number) {
  switch (statusCode) {
    case 404: return 'O recurso solicitado não está disponível. Verifique a URL ou retorne ao painel principal.'
    case 500: return 'Ocorreu um erro interno no sistema. Nossa equipe técnica foi notificada e está trabalhando na solução.'
    case 403: return 'Você não tem permissão para acessar este recurso.'
    case 401: return 'Sua sessão expirou ou você não está autenticado. Faça login novamente para continuar.'
    default: return 'Ocorreu um erro inesperado. Tente novamente.'
  }
}

function handlePrimary() {
  switch (error?.statusCode) {
    case 500:
      window.location.reload()
      break
    case 401:
      clearError()
      navigateTo('/signout')
      break
    default:
      clearError()
      navigateTo('/')
  }
}

function handleSecondary() {
  clearError()
  navigateTo('/')
}

const primaryLabel = error?.statusCode === 500
  ? 'Atualizar página'
  : error?.statusCode === 401
    ? 'Fazer login'
    : 'Ir ao início'

const primaryIcon = error?.statusCode === 500
  ? 'i-lucide-refresh-cw'
  : error?.statusCode === 401
    ? 'i-lucide-log-in'
    : 'i-lucide-home'

const showSecondary = error?.statusCode === 500 || error?.statusCode === 401

useHead({
  title: getErrorTitle(error?.statusCode),
  meta: [{ name: 'description', content: getErrorDescription(error?.statusCode) }]
})
</script>

<template>
  <UContainer class="min-h-screen flex items-center justify-center p-4">
    <div class="w-full max-w-2xl">
      <div class="text-center mb-8">
        <div class="flex items-center justify-center gap-6 mb-6">
          <span class="text-5xl font-bold px-6 py-3 rounded-2xl border-2 border-primary text-primary">
            {{ error?.statusCode || '?' }}
          </span>
          <UIcon
            name="i-lucide-heart-pulse"
            class="w-16 h-16 text-primary animate-pulse"
          />
        </div>
      </div>

      <UCard>
        <template #header>
          <div class="text-center">
            <h1 class="text-3xl font-bold mb-2">
              {{ getErrorTitle(error?.statusCode) }}
            </h1>
            <UIcon
              name="i-lucide-stethoscope"
              class="w-8 h-8 text-primary mx-auto"
            />
          </div>
        </template>

        <div class="text-center space-y-6">
          <p class="text-lg text-muted leading-relaxed max-w-lg mx-auto">
            {{ getErrorDescription(error?.statusCode) }}
          </p>

          <div class="flex items-center justify-center gap-3">
            <UButton
              :icon="primaryIcon"
              size="lg"
              @click="handlePrimary"
            >
              {{ primaryLabel }}
            </UButton>
            <UButton
              v-if="showSecondary"
              icon="i-lucide-home"
              size="lg"
              variant="outline"
              color="neutral"
              @click="handleSecondary"
            >
              Ir ao início
            </UButton>
          </div>
        </div>
      </UCard>

      <div class="mt-8 text-center">
        <div class="flex items-center justify-center gap-2 text-sm text-muted">
          <UIcon
            name="i-lucide-shield-check"
            class="w-4 h-4"
          />
        </div>
      </div>
    </div>
  </UContainer>
</template>
